import { useState, useEffect, useMemo, useRef } from 'react';
import { serverTimeService } from '@/api/domains/serverTime';
import { formatDateToYYYYMMDD } from '@/utils';


const TICK_INTERVAL_MS = 60_000;

export interface UseServerTimeSyncReturn {
  maxDate: string;
  fallbackDate: string;
  /** Live server-corrected time in HH:mm format */
  currentTime: string;
}

export function useServerTimeSync(): UseServerTimeSyncReturn {
  const [serverBaseMs, setServerBaseMs] = useState<number | null>(null);
  const [clientBaseMs, setClientBaseMs] = useState<number | null>(null);
  const [tickMs, setTickMs] = useState<number>(() => Date.now());
  const tickTimerRef = useRef<number | null>(null);

  const fallbackDate = useMemo(() => formatDateToYYYYMMDD(new Date()), []);

  // Load server time once
  useEffect(() => {
    let mounted = true;
    void (async () => {
      const response = await serverTimeService.get();
      if (response.success && response.data?.localDateTime) {
        const serverNow = new Date(response.data.localDateTime);
        if (!isNaN(serverNow.getTime()) && mounted) {
          setServerBaseMs(serverNow.getTime());
          setClientBaseMs(Date.now());
          setTickMs(serverNow.getTime());
        }
      }
    })();
    return () => { mounted = false; };
  }, []);

  // Aligned minute tick: wait until next minute boundary, then tick every 60s
  useEffect(() => {
    if (serverBaseMs == null || clientBaseMs == null) return;

    const updateFromOffset = () => {
      const current = new Date(serverBaseMs + (Date.now() - clientBaseMs));
      setTickMs(current.getTime());
    };

    // Align to next minute boundary using current server-offset time
    const current = new Date(serverBaseMs + (Date.now() - clientBaseMs));
    const msToNextMinute = (60 - current.getSeconds()) * 1000 - current.getMilliseconds();

    const startTimeout = window.setTimeout(() => {
      updateFromOffset();
      tickTimerRef.current = window.setInterval(
        updateFromOffset,
        TICK_INTERVAL_MS,
      ) as unknown as number;
    }, Math.max(0, msToNextMinute));

    return () => {
      window.clearTimeout(startTimeout);
      if (tickTimerRef.current) window.clearInterval(tickTimerRef.current);
      tickTimerRef.current = null;
    };
  }, [serverBaseMs, clientBaseMs]);

  // Derive maxDate and currentTime from reactive tickMs
  const maxDate = useMemo(() => formatDateToYYYYMMDD(new Date(tickMs)), [tickMs]);

  const currentTime = useMemo(() => {
    const d = new Date(tickMs);
    return `${d.getHours().toString().padStart(2, '0')}:${d.getMinutes().toString().padStart(2, '0')}`;
  }, [tickMs]);

  return {
    maxDate,
    fallbackDate,
    currentTime,
  };
}
